<script setup lang="ts">
/**
 * H7 — AI onboarding block (profile completeness / survey CTA)
 * Figma RedisignHomePage `15:9800` / compact `15:9803`
 * Mascot = Figma frames `15:9322` (desktop) / `15:9435` (compact) rendered whole
 * (eyes are separate SVG layers in Figma → must be exported together with the figure)
 * H10 — live persona + score from personalization/status
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthModal } from '@/features/auth/store'
import { useTasteClarificationModal } from '@/features/taste/store'
import { PROFILE_ROUTE } from '@/router/routes'
import { AiraRiveFigure, AIRA_FULL_RIVE_SRC } from '@/features/aira-widget'
import { FairAiButton } from '@/ui/primitives/FairAiButton'
import { FairLink } from '@/ui/primitives/FairLink'
import {
  FAIR_HOME_V2_AI_DEFAULT_PERSONA,
  formatFairHomeV2AiCompleteness,
  resolveFairHomeV2AiPersona,
  type FairHomeV2AiPersonaId,
} from './fair-home-v2-ai.data'
import './fair-home-v2-ai.css'

const props = withDefaults(
  defineProps<{
    compact?: boolean
    persona?: FairHomeV2AiPersonaId
    completenessPercent?: number
  }>(),
  {
    compact: false,
    persona: FAIR_HOME_V2_AI_DEFAULT_PERSONA,
    completenessPercent: undefined,
  },
)

const { t } = useI18n()
const router = useRouter()
const authModal = useAuthModal()
const tasteStore = useTasteClarificationModal()

const personaView = computed(() =>
  resolveFairHomeV2AiPersona(props.persona, {
    completenessPercent: props.completenessPercent,
  }),
)

const completenessLabel = computed(() =>
  formatFairHomeV2AiCompleteness(personaView.value.completenessPercent),
)

const descriptionText = computed(() =>
  t(personaView.value.descriptionKey, {
    percent: personaView.value.completenessPercent,
  }),
)

const levelLine = computed(() =>
  t('fair_home_v2.ai.level_line', {
    level: t(personaView.value.levelNameKey),
    step: personaView.value.levelStep,
    total: personaView.value.levelTotal,
    nextStep: t(personaView.value.nextStepKey),
  }),
)

const progressSegments = computed(() =>
  Array.from(
    { length: personaView.value.progressTotalSegments },
    (_, index) => index < personaView.value.progressFilledSegments,
  ),
)

const rootClass = computed(() => ({
  'fair-home-v2-ai': true,
  'fair-home-v2-ai--compact': props.compact,
  'fair-home-v2-ai--desktop': !props.compact,
}))

const buttonSize = computed(() => (props.compact ? 'mobile' : 'medium'))

function handleCtaClick() {
  const action = personaView.value.ctaAction
  if (action === 'auth') {
    authModal.showAuthModal('signInSignUpWrapper')
    return
  }
  if (action === 'taste') {
    void tasteStore.showTasteModal()
    return
  }
  void router.push(PROFILE_ROUTE)
}

function handleSignInClick() {
  authModal.showAuthModal('signInSignUpWrapper')
}
</script>

<template>
  <section
    :class="rootClass"
    aria-label="AI profile"
  >
    <div class="fair-home-v2-ai__panel">
      <div
        v-if="!compact"
        class="fair-home-v2-ai__mascot"
        aria-hidden="true"
      >
        <AiraRiveFigure
          class="fair-home-v2-ai__mascot-figure"
          :src="AIRA_FULL_RIVE_SRC"
          property="thinks"
          :width="349"
          :height="669"
        />
      </div>

      <div class="fair-home-v2-ai__content">
        <div class="fair-home-v2-ai__intro">
          <div class="fair-home-v2-ai__copy">
            <h2 class="fair-home-v2-ai__title">
              {{ t('fair_home_v2.ai.title') }}
            </h2>
            <p class="fair-home-v2-ai__description">
              {{ descriptionText }}
            </p>
          </div>

          <div
            v-if="compact"
            class="fair-home-v2-ai__mascot-compact"
            aria-hidden="true"
          >
            <AiraRiveFigure
              class="fair-home-v2-ai__mascot-compact-figure"
              :src="AIRA_FULL_RIVE_SRC"
              property="thinks"
              :width="190"
              :height="363"
            />
          </div>
        </div>

        <div class="fair-home-v2-ai__progress">
          <div class="fair-home-v2-ai__progress-meta">
            <p class="fair-home-v2-ai__progress-label">
              {{ t('fair_home_v2.ai.profile_completeness') }}
            </p>
            <p class="fair-home-v2-ai__progress-value">
              {{ completenessLabel }}
            </p>
          </div>

          <div
            class="fair-home-v2-ai__progress-track"
            role="progressbar"
            :aria-valuenow="personaView.completenessPercent"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-label="t('fair_home_v2.ai.profile_completeness')"
          >
            <div
              v-for="(filled, index) in progressSegments"
              :key="index"
              class="fair-home-v2-ai__progress-segment"
              :class="{ 'fair-home-v2-ai__progress-segment--filled': filled }"
            />
          </div>

          <p class="fair-home-v2-ai__level-line">
            {{ levelLine }}
          </p>
        </div>

        <div class="fair-home-v2-ai__actions">
          <FairAiButton
            class="fair-home-v2-ai__cta"
            :size="buttonSize"
            animated
            :label="t(personaView.ctaKey)"
            @click="handleCtaClick"
          />

          <div
            v-if="personaView.showSignIn"
            class="fair-home-v2-ai__sign-in"
          >
            <p class="fair-home-v2-ai__sign-in-text">
              {{ t('fair_home_v2.ai.already_with_us') }}
            </p>
            <FairLink
              type="static"
              :label="t('fair_home_v2.ai.link_sign_in')"
              @click="handleSignInClick"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
